import "./presupuesto.css";

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Formik } from "formik";
import ReactGA from "react-ga4";
import Swal from "sweetalert2";

import { validateModel } from "./validationSchema";
import { fetchExternal } from "../../utils/fetch";

const TIPO_COBERTURA = {
  "Particular": 1,
  "Obra social/Prepaga": 2,
};

const TIPO_DOCUMENTO_ADJUNTO = {
  ORDEN_MEDICA: 1,
  CREDENCIAL_COBERTURA: 2,
  DOCUMENTACION_ADICIONAL: 3,
};

const NOMENCLATURA_ARCHIVO = {
  [TIPO_DOCUMENTO_ADJUNTO.ORDEN_MEDICA]: "AOM",
  [TIPO_DOCUMENTO_ADJUNTO.CREDENCIAL_COBERTURA]: "CCA",
  [TIPO_DOCUMENTO_ADJUNTO.DOCUMENTACION_ADICIONAL]: "DA",
};

const ARCHIVOS_POR_COBERTURA = {
  Particular: [
    { field: "ordenMedica", idTipoDocumentoAdjunto: TIPO_DOCUMENTO_ADJUNTO.ORDEN_MEDICA },
    { field: "documentacionAdicional", idTipoDocumentoAdjunto: TIPO_DOCUMENTO_ADJUNTO.DOCUMENTACION_ADICIONAL },
  ],
  "Obra social/Prepaga": [
    { field: "credencialCobertura", idTipoDocumentoAdjunto: TIPO_DOCUMENTO_ADJUNTO.CREDENCIAL_COBERTURA },
    { field: "ordenMedica", idTipoDocumentoAdjunto: TIPO_DOCUMENTO_ADJUNTO.ORDEN_MEDICA },
    { field: "documentacionAdicional", idTipoDocumentoAdjunto: TIPO_DOCUMENTO_ADJUNTO.DOCUMENTACION_ADICIONAL },
  ],
};

const tiposDocumento = [
  { value: "", label: "Tipo de doc" },
  { value: "0", label: "DNI" },
  { value: "1", label: "Pasaporte" }  
];

const tiposCobertura = [
  { value: "", label: "Tipo de cobertura" },
  { value: "Obra social/Prepaga", label: "Obra social/Prepaga" },
  { value: "Particular", label: "Particular" },
];

const tiposAtencionObraSocial = [
  { value: "", label: "Tipo de atención médica" },
  { value: "Consulta Ambulatoria", label: "Consulta Ambulatoria" },
  { value: "Diagnóstico por imagen", label: "Diagnóstico por imagen" },
  { value: "Estudio médico", label: "Estudio médico" },
];

const tiposAtencionParticular = [
  { value: "", label: "Tipo de atención médica" },
  { value: "Consulta Ambulatoria", label: "Consulta Ambulatoria" },
  { value: "Diagnóstico por imagen", label: "Diagnóstico por imagen" },
  { value: "Estudio médico", label: "Estudio médico" },
];

const PresupuestoPage = () => {
  const ordenMedicaRef = useRef(null);
  const docAdicionalRef = useRef(null);
  const credencialCoberturaRef = useRef(null);
  const [ordenMedicaName, setOrdenMedicaName] = useState("");
  const [ordenMedicaNames, setOrdenMedicaNames] = useState([]);
  const [docAdicionalName, setDocAdicionalName] = useState("");
  const [docAdicionalNames, setDocAdicionalNames] = useState([]);
  const [credencialCoberturaName, setCredencialCoberturaName] = useState("");
  const [credencialCoberturaNames, setCredencialCoberturaNames] = useState([]);
  const [obraSocialData, setObraSocialData] = useState(null);
  const [loadingObraSocial, setLoadingObraSocial] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchObraSocial = async (tipoDocumento, numero, setFieldValue, tipoCobertura) => {
    if (!tipoDocumento || !numero) return;
    try {
      setLoadingObraSocial(true);
      const baseUrl = process.env.REACT_APP_BASE_URL_GW_API_V2;
      const { data: response } = await axios.get(
        `${baseUrl}/customers/api/v2/getsocialsecuritybydnifromdb`,
        { params: { dniType: tipoDocumento, dni: numero } }
      );
      setObraSocialData(response);
      if (tipoCobertura === 'Obra social/Prepaga' && setFieldValue) {
        const { socialSecurity, socialSecurityPlan } = response?.data ?? {};
        setFieldValue('nombreAseguradora', socialSecurity ?? '');
        setFieldValue('planCobertura', socialSecurityPlan ?? '');
      }
    } catch (error) {
      setObraSocialData(null);
      if (tipoCobertura === 'Obra social/Prepaga' && setFieldValue) {
        setFieldValue('nombreAseguradora', '');
        setFieldValue('planCobertura', '');
      }
    } finally {
      setLoadingObraSocial(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const initialValues = {
    nombre: "",
    apellido: "",
    tipoDocumento: "",
    numero: "",
    email: "",
    tipoCobertura: "",
    nombreAseguradora: "",
    planCobertura: "",
    tipoAtencion: "",
    estudio: "",
    credencialCobertura: [],
    ordenMedica: [],
    documentacionAdicional: [],
    informacionAdicional: "",
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const mapFilesToArchivos = async (files, idTipoDocumentoAdjunto) => {
    if (!files?.length) return [];
    return Promise.all(
      files.map(async (file) => ({
        idTipoDocumentoAdjunto,
        nomenclatura: NOMENCLATURA_ARCHIVO[idTipoDocumentoAdjunto],
        nombreArchivo: file.name,
        base64File: await fileToBase64(file),
      }))
    );
  };

  const buildArchivosPayload = async (tipoCobertura, values) => {
    const archivosConfig = ARCHIVOS_POR_COBERTURA[tipoCobertura] || [];
    const archivos = [];

    for (const { field, idTipoDocumentoAdjunto } of archivosConfig) {
      archivos.push(
        ...(await mapFilesToArchivos(values[field], idTipoDocumentoAdjunto))
      );
    }

    return archivos;
  };

  const onSubmit = async (values, resetForm) => {
    const esObraSocialPrepaga = values.tipoCobertura === "Obra social/Prepaga";
    setSubmitting(true);
    try {
      const archivos = await buildArchivosPayload(values.tipoCobertura, values);

      const savePayload = {
        nombre: values.nombre,
        apellido: values.apellido,
        tipoDocumento: values.tipoDocumento,
        numeroDocumento: values.numero,
        email: values.email,
        idTipoCobertura: TIPO_COBERTURA[values.tipoCobertura],
        tipoAtencionMedica: values.tipoAtencion,
        estudioSolicitado: values.estudio,
        informacionAdicional: values.informacionAdicional || "",
        archivos,
      };

      await fetchExternal("patient/Api/v1/solicitudPresupuesto/Save", "POST", savePayload);

      const emailPayload = {
        nombre: values.nombre,
        apellido: values.apellido,
        tipoDocumento: values.tipoDocumento,
        numero: values.numero,
        email: values.email,
        tipoCobertura: values.tipoCobertura,
        ...(esObraSocialPrepaga && {
          nombreAseguradora: values.nombreAseguradora,
          planCobertura: values.planCobertura,
        }),
        tipoAtencion: values.tipoAtencion,
        estudio: values.estudio,
        informacionAdicional: values.informacionAdicional,
        ordenMedica: archivos
          .filter((a) => a.idTipoDocumentoAdjunto === TIPO_DOCUMENTO_ADJUNTO.ORDEN_MEDICA)
          .map((a) => a.base64File),
        documentacionAdicional: archivos
          .filter((a) => a.idTipoDocumentoAdjunto === TIPO_DOCUMENTO_ADJUNTO.DOCUMENTACION_ADICIONAL)
          .map((a) => a.base64File),
        ...(esObraSocialPrepaga && {
          credencialCobertura: archivos
            .filter((a) => a.idTipoDocumentoAdjunto === TIPO_DOCUMENTO_ADJUNTO.CREDENCIAL_COBERTURA)
            .map((a) => a.base64File),
        }),
      };

      if (!emailPayload.ordenMedica.length) emailPayload.ordenMedica = null;
      if (!emailPayload.documentacionAdicional.length) emailPayload.documentacionAdicional = null;
      if (esObraSocialPrepaga && !emailPayload.credencialCobertura?.length) {
        emailPayload.credencialCobertura = null;
      }

      const baseUrl = process.env.REACT_APP_BASE_URL_GW_API_V2;
      await axios.post(
        `${baseUrl}/customers/api/v2/SubmitBudgetRequest`,
        emailPayload
      );

      ReactGA.event({
        category: "Formulario Presupuesto",
        action: "Clic",
        label: "Envío de Formulario",
      });

      resetForm();
      setOrdenMedicaName("");
      setOrdenMedicaNames([]);
      setDocAdicionalName("");
      setDocAdicionalNames([]);
      setCredencialCoberturaName("");
      setCredencialCoberturaNames([]);
      if (ordenMedicaRef.current) ordenMedicaRef.current.value = "";
      if (docAdicionalRef.current) docAdicionalRef.current.value = "";
      if (credencialCoberturaRef.current) credencialCoberturaRef.current.value = "";

      Swal.fire({
        html: `
          <div class="presupuesto-swal-content">
            <div class="presupuesto-swal-icon">
              <i class="fas fa-check"></i>
            </div>
            <h2 class="presupuesto-swal-title">¡Formulario enviado!</h2>
            <p class="presupuesto-swal-text">Hemos recibido tu solicitud de presupuesto correctamente, pronto estaremos en contacto contigo.</p>
          </div>
        `,
        showConfirmButton: true,
        confirmButtonText: "Cerrar",
        customClass: {
          popup: "presupuesto-swal-popup",
          confirmButton: "presupuesto-swal-btn",
        },
        buttonsStyling: false,
      });
    } catch (error) {
      Swal.fire(
        "Error",
        "Ocurrió un error al enviar la solicitud. Intente nuevamente.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const ACCEPTED_FILES = ".pdf,.jpg,.jpeg";

  return (
    <div className="presupuesto-page">
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validateModel}
        onSubmit={(values, { resetForm }) => {
          onSubmit(values, resetForm);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
        }) => {
          const esObraSocialPrepaga = values.tipoCobertura === "Obra social/Prepaga";
          const tiposAtencion = esObraSocialPrepaga ? tiposAtencionObraSocial : tiposAtencionParticular;

          const totalArchivos = 
            (values.ordenMedica?.length || 0) + 
            (values.credencialCobertura?.length || 0) + 
            (values.documentacionAdicional?.length || 0);
          const MAX_ARCHIVOS = 4;

          return (
          <div className="presupuesto-card">
            {/* Título */}
            <h1 className="presupuesto-title">Solicitud de presupuesto</h1>
            <div className="presupuesto-title-line" />
            <p className="presupuesto-subtitle">
              Estos datos deben coincidir con los de la orden médica y/o la
              credencial de cobertura.
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              noValidate
            >
              {/* Fila 1: Nombre, Apellido, Tipo doc, Número */}
              <div className="presupuesto-row">
                <div className="presupuesto-col-quarter presupuesto-field">
                  <label className="presupuesto-label">
                    Nombre <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="nombre"
                    className="presupuesto-input"
                    placeholder="Nombre"
                    value={values.nombre}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[0-9]/g, '');
                      setFieldValue('nombre', val);
                    }}
                    onBlur={handleBlur}
                  />
                  <div className="presupuesto-error">
                    {touched.nombre && errors.nombre}
                  </div>
                </div>

                <div className="presupuesto-col-quarter presupuesto-field">
                  <label className="presupuesto-label">
                    Apellido <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="apellido"
                    className="presupuesto-input"
                    placeholder="Apellido"
                    value={values.apellido}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[0-9]/g, '');
                      setFieldValue('apellido', val);
                    }}
                    onBlur={handleBlur}
                  />
                  <div className="presupuesto-error">
                    {touched.apellido && errors.apellido}
                  </div>
                </div>

                <div className="presupuesto-col-quarter presupuesto-field">
                  <label className="presupuesto-label">
                    Tipo de documento <span className="required">*</span>
                  </label>
                  <select
                    name="tipoDocumento"
                    className="presupuesto-select"
                    value={values.tipoDocumento}
                    onChange={(e) => {
                      handleChange(e);
                      if (values.numero && !errors.numero) {
                        fetchObraSocial(e.target.value, values.numero, setFieldValue, values.tipoCobertura);
                      }
                    }}
                    onBlur={handleBlur}
                  >
                    {tiposDocumento.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <div className="presupuesto-error">
                    {touched.tipoDocumento && errors.tipoDocumento}
                  </div>
                </div>

                <div className="presupuesto-col-quarter presupuesto-field">
                  <label className="presupuesto-label">
                    Número <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="numero"
                    className="presupuesto-input"
                    placeholder="Número"
                    value={values.numero}
                    maxLength={15}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      setFieldValue('numero', val);
                    }}
                    onBlur={(e) => {
                      handleBlur(e);
                      if (values.tipoDocumento && e.target.value && !errors.numero) {
                        fetchObraSocial(values.tipoDocumento, e.target.value, setFieldValue, values.tipoCobertura);
                      }
                    }}
                  />
                  <div className="presupuesto-error">
                    {touched.numero && errors.numero}
                  </div>
                </div>
              </div>

              {/* Fila 2: Email, Tipo cobertura */}
              <div className="presupuesto-row">
                <div className="presupuesto-col-half presupuesto-field">
                  <label className="presupuesto-label">
                    Email<span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    className="presupuesto-input"
                    placeholder="Email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <div className="presupuesto-error">
                    {touched.email && errors.email}
                  </div>
                </div>

                <div className="presupuesto-col-half presupuesto-field">
                  <label className="presupuesto-label">
                    Seleccione su tipo de cobertura{" "}
                    <span className="required">*</span>
                  </label>
                  <select
                    name="tipoCobertura"
                    className="presupuesto-select"
                    value={values.tipoCobertura}
                    onChange={(e) => {
                      handleChange(e);
                      const seleccion = e.target.value;
                      // Si elige Obra social y hay datos del paciente, autocompletar
                      if (seleccion === "Obra social/Prepaga") {
                        const { socialSecurity, socialSecurityPlan } = obraSocialData?.data ?? {};
                        setFieldValue("nombreAseguradora", socialSecurity ?? "");
                        setFieldValue("planCobertura", socialSecurityPlan ?? "");
                      } else {
                        setFieldValue("nombreAseguradora", "");
                        setFieldValue("planCobertura", "");
                      }
                      setFieldValue("tipoAtencion", "");
                      setFieldValue("credencialCobertura", []);
                      setCredencialCoberturaName("");
                      setCredencialCoberturaNames([]);
                      if (credencialCoberturaRef.current) credencialCoberturaRef.current.value = "";
                    }}
                    onBlur={handleBlur}
                  >
                    {tiposCobertura.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <div className="presupuesto-error">
                    {touched.tipoCobertura && errors.tipoCobertura}
                  </div>
                </div>
              </div>

              {/* Fila condicional: Aseguradora y Plan (solo Obra social/Prepaga) */}
              {esObraSocialPrepaga && (
                <div className="presupuesto-row">
                  <div className="presupuesto-col-half presupuesto-field">
                    <label className="presupuesto-label">
                      Nombre de la aseguradora <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="nombreAseguradora"
                      className="presupuesto-input"
                      placeholder="Ej: OSDE, Swiss Medical"
                      value={values.nombreAseguradora}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <div className="presupuesto-error">
                      {touched.nombreAseguradora && errors.nombreAseguradora}
                    </div>
                  </div>

                  <div className="presupuesto-col-half presupuesto-field">
                    <label className="presupuesto-label">
                      Plan de cobertura <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="planCobertura"
                      className="presupuesto-input"
                      placeholder="Ej: 210, 310"
                      value={values.planCobertura}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <div className="presupuesto-error">
                      {touched.planCobertura && errors.planCobertura}
                    </div>
                  </div>
                </div>
              )}

              {/* Fila 3: Tipo atención, Estudio */}
              <div className="presupuesto-row">
                <div className="presupuesto-col-half presupuesto-field" style={{ marginBottom: 0 }}>
                  <label className="presupuesto-label">
                    Seleccione su tipo de atención medica{" "}
                    <span className="required">*</span>
                  </label>
                  <select
                    name="tipoAtencion"
                    className="presupuesto-select"
                    value={values.tipoAtencion}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    {tiposAtencion.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <div className="presupuesto-error">
                    {touched.tipoAtencion && errors.tipoAtencion}
                  </div>
                </div>

                <div className="presupuesto-col-half presupuesto-field" style={{ marginBottom: 0 }}>
                  <label className="presupuesto-label">
                    Describa el estudio que necesita{" "}
                    <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="estudio"
                    className="presupuesto-input"
                    placeholder="Describa el estudio que necesita"
                    value={values.estudio}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <div className="presupuesto-error">
                    {touched.estudio && errors.estudio}
                  </div>
                </div>
              </div>

              {/* Nota de formatos */}
              <p className="presupuesto-note">
                <span style={{ fontWeight: 600 }}>Cuantos más datos adjunte, más ágil será el tiempo de respuesta
                de su presupuesto.</span>{" "}
                Formatos admitidos:{" "}
                <span className="formats">PDF, JPG, JPEG</span>.
              </p>

              {/* Fila 4: Archivos adjuntos */}
              <div className="presupuesto-row">
                {/* Credencial de cobertura (solo Obra social/Prepaga) */}
                {esObraSocialPrepaga && (
                  <div className={`${esObraSocialPrepaga ? "presupuesto-col-third" : "presupuesto-col-half"} presupuesto-field`}>
                    <label className="presupuesto-label">
                      Credencial de cobertura adjunta <span className="required">*</span>
                    </label>
                    <div className="presupuesto-file-wrapper">
                      <button
                        type="button"
                        className="presupuesto-file-btn"
                        onClick={() => credencialCoberturaRef.current?.click()}
                      >
                        <i className="fas fa-paperclip" /> Adjuntar
                      </button>
                      <span className="presupuesto-file-name"></span>
                      <input
                        ref={credencialCoberturaRef}
                        type="file"
                        accept={ACCEPTED_FILES}
                        className="presupuesto-file-input"
                        onChange={(e) => {
                          const file = e.currentTarget.files?.[0];
                          if (file) {
                            setFieldValue("credencialCobertura", [file]);
                            setFieldTouched("credencialCobertura", true, false);
                            setCredencialCoberturaNames([file.name]);
                          }
                          e.currentTarget.value = "";
                        }}
                      />
                    </div>
                    {credencialCoberturaNames.length > 0 && (
                      <ul className="presupuesto-file-list">
                        {credencialCoberturaNames.map((name, i) => (
                          <li key={i}>
                            {name}
                            <button type="button" onClick={() => {
                              const updated = [...(values.credencialCobertura || [])];
                              updated.splice(i, 1);
                              setFieldValue("credencialCobertura", updated);
                              setCredencialCoberturaNames(updated.map(f => f.name));
                            }}>x</button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="presupuesto-error">
                      {touched.credencialCobertura && errors.credencialCobertura}
                    </div>
                  </div>
                )}

                <div className={`${esObraSocialPrepaga ? "presupuesto-col-third" : "presupuesto-col-half"} presupuesto-field`}>
                  <label className="presupuesto-label">
                    Adjuntar orden médica <span className="required">*</span>
                  </label>
                  <div className="presupuesto-file-wrapper">
                    <button
                      type="button"
                      className="presupuesto-file-btn"
                      onClick={() => ordenMedicaRef.current?.click()}
                    >
                      <i className="fas fa-paperclip" /> Adjuntar
                    </button>
                    <span className="presupuesto-file-name"></span>
                    <input
                      ref={ordenMedicaRef}
                      type="file"
                      accept={ACCEPTED_FILES}
                      multiple
                      className="presupuesto-file-input"
                      onChange={(e) => {
                        const archivosOtros = (values.credencialCobertura?.length || 0) + (values.documentacionAdicional?.length || 0);
                        const disponibles = MAX_ARCHIVOS - archivosOtros;
                        const newFiles = Array.from(e.currentTarget.files || []).slice(0, disponibles);
                        const merged = [...(values.ordenMedica || []), ...newFiles].slice(0, disponibles);
                        setFieldValue("ordenMedica", merged.length ? merged : []);
                        setFieldTouched("ordenMedica", true, false);
                        setOrdenMedicaNames(merged.map(f => f.name));
                        e.currentTarget.value = "";
                      }}
                    />
                  </div>
                  {ordenMedicaNames.length > 0 && (
                    <ul className="presupuesto-file-list">
                      {ordenMedicaNames.map((name, i) => (
                        <li key={i}>
                          {name}
                          <button type="button" onClick={() => {
                            const updated = [...(values.ordenMedica || [])];
                            updated.splice(i, 1);
                            setFieldValue("ordenMedica", updated);
                            setOrdenMedicaNames(updated.map(f => f.name));
                          }}>x</button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="presupuesto-error">
                    {touched.ordenMedica && errors.ordenMedica}
                  </div>
                </div>

                <div className={`${esObraSocialPrepaga ? "presupuesto-col-third" : "presupuesto-col-half"} presupuesto-field`}>
                  <label className="presupuesto-label">
                    Documentación adicional (Opcional)
                  </label>
                  <div className="presupuesto-file-wrapper">
                    <button
                      type="button"
                      className="presupuesto-file-btn"
                      onClick={() => docAdicionalRef.current?.click()}
                    >
                      <i className="fas fa-paperclip" /> Adjuntar
                    </button>
                    <span className="presupuesto-file-name"></span>
                    <input
                      ref={docAdicionalRef}
                      type="file"
                      accept={ACCEPTED_FILES}
                      className="presupuesto-file-input"
                      onChange={(e) => {
                        const archivosOtros = (values.ordenMedica?.length || 0) + (values.credencialCobertura?.length || 0);
                        const disponibles = Math.min(1, MAX_ARCHIVOS - archivosOtros);
                        const files = Array.from(e.currentTarget.files || []).slice(0, disponibles);
                        setFieldValue("documentacionAdicional", files.length ? files : []);
                        setDocAdicionalNames(files.map(f => f.name));
                        e.currentTarget.value = "";
                      }}
                    />
                  </div>
                  {docAdicionalNames.length > 0 && (
                    <ul className="presupuesto-file-list">
                      {docAdicionalNames.map((name, i) => (
                        <li key={i}>
                          {name}
                          <button type="button" onClick={() => {
                            const updated = [...(values.documentacionAdicional || [])];
                            updated.splice(i, 1);
                            setFieldValue("documentacionAdicional", updated);
                            setDocAdicionalNames(updated.map(f => f.name));
                          }}>x</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Fila 5: Información adicional */}
              <div className="presupuesto-row">
                <div className="presupuesto-col-half presupuesto-field">
                  <label className="presupuesto-label" style={{ color: "#1a5dab" }}>
                    Información adicional (Opcional)
                  </label>
                  <textarea
                    name="informacionAdicional"
                    className="presupuesto-textarea"
                    placeholder="Toda información que considere necesario agregar"
                    value={values.informacionAdicional}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    rows="1"
                  />
                </div>
              </div>

              {/* Botón */}
              <button type="submit" className="presupuesto-submit" disabled={submitting}>
                {submitting ? (
                  <><i className="fas fa-spinner fa-spin" /> Enviando...</>
                ) : (
                  "Enviar solicitud de presupuesto"
                )}
              </button>
            </form>
          </div>
        )}}
      </Formik>
    </div>
  );
};

export default PresupuestoPage;
